import { Alert, Button, Col, Divider, Form, Row, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { PhoneInput } from "../../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../../constants/placeholders"
import { normalizeEmail } from "../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../utils/validation/validate-phone.ts"
import type { LeadFormData } from "../components/lead-form-data.ts"
import { LeadWizardCustomerSearch } from "./LeadWizardCustomerSearch.tsx"
import { LeadWizardDuplicateWarning } from "./LeadWizardDuplicateWarning.tsx"
import { type CustomerMatchCandidate, candidateKey } from "./customer-match.ts"
import { buildUnifiedDuplicateMatches } from "./duplicate-match.ts"
import type { LinkedCustomer } from "./linked-customer.ts"
import { useCustomerMatches } from "./use-customer-matches.ts"
import { useLeadMatches } from "./use-lead-matches.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { SmsConsentDisclosure } from "../../../../components/SmsConsentDisclosure/SmsConsentDisclosure.tsx"

interface LeadWizardCustomerStepProps {
  form: FormInstance<LeadFormData>
  linked: LinkedCustomer | null
  busyKey: string | null
  onUse: (candidate: CustomerMatchCandidate) => void
  onCreateNew: () => void
  onCreateAnyway: () => void
  onOpenExistingLead: (id: string) => void
}

export function LeadWizardCustomerStep({
  form,
  linked,
  busyKey,
  onUse,
  onCreateNew,
  onCreateAnyway,
  onOpenExistingLead
}: LeadWizardCustomerStepProps) {
  const { t } = useTranslation()
  const [customerDismissed, setCustomerDismissed] = useState<string | null>(
    null
  )
  const [leadDismissed, setLeadDismissed] = useState(false)

  const typed = Form.useWatch(
    (values) => ({
      first_name: values?.customer?.first_name,
      last_name: values?.customer?.last_name,
      email: values?.customer?.email,
      phone: values?.customer?.phone
    }),
    form
  )

  const { matches } = useCustomerMatches({
    first_name: typed?.first_name,
    last_name: typed?.last_name,
    email: typed?.email,
    phone: typed?.phone
  })

  // Runs whether or not a customer is linked: linking fills these very fields,
  // so the commonest way into the wizard would otherwise never be checked.
  const { matches: leadMatches } = useLeadMatches({
    first_name: typed?.first_name,
    last_name: typed?.last_name,
    email: typed?.email,
    phone: typed?.phone
  })

  const customerSignature = matches.map(candidateKey).join(";")

  // One row per real-world duplicate signal, so a customer who both matched
  // and already has a lead never surfaces as two separate cards.
  const unified = buildUnifiedDuplicateMatches(matches, leadMatches, {
    email: typed?.email,
    phone: typed?.phone
  })

  // Dismissed for the whole submission, not for the matches as they stand:
  // the list changes with every keystroke, so a signature-keyed dismissal
  // would reopen the panel as soon as the user types on.
  const leadRows = leadDismissed
    ? []
    : unified.filter((row) => row.kind !== "customer")
  // The customer axis re-arms as soon as the matched set changes, and it never
  // shows once a customer has been linked — linking already resolved it.
  const customerRows =
    linked?.id || customerSignature === customerDismissed
      ? []
      : unified.filter((row) => row.kind === "customer")
  const visibleRows = [...leadRows, ...customerRows]

  const handleCreateNewCustomer = () => {
    setCustomerDismissed(customerSignature)
    onCreateNew()
  }

  const handleCreateAnyway = () => {
    setLeadDismissed(true)
    onCreateAnyway()
  }

  return (
    <>
      {!linked?.id && (
        <LeadWizardCustomerSearch busyKey={busyKey} onUse={onUse} />
      )}

      {linked?.id && (
        <Alert
          type={"success"}
          showIcon
          className={"mb-4"}
          message={t("leadWizard.customer.linked")}
          action={
            <Button size={"small"} onClick={onCreateNew}>
              {t("leadWizard.customer.unlink")}
            </Button>
          }
        />
      )}

      <LeadWizardDuplicateWarning
        rows={visibleRows}
        busyKey={busyKey}
        onUseExisting={onUse}
        onCreateNewCustomer={handleCreateNewCustomer}
        onOpenExistingLead={(lead) => lead.id && onOpenExistingLead(lead.id)}
        onCreateAnyway={handleCreateAnyway}
      />

      {!linked?.id && (
        <>
          <Divider plain>{t("leadWizard.customer.or")}</Divider>

          <Typography.Title level={5}>
            {t("leadWizard.customer.sectionTitle")}
          </Typography.Title>
        </>
      )}

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.customer.firstName")}
            name={["customer", "first_name"]}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("leadWizard.customer.firstNameRequired")
              }
            ]}
          >
            <TextInput
              placeholder={t("leadWizard.customer.firstNamePlaceholder")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.customer.lastName")}
            name={["customer", "last_name"]}
          >
            <TextInput
              placeholder={t("leadWizard.customer.lastNamePlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            extra={<SmsConsentDisclosure />}
            label={t("leadWizard.customer.phoneOptional")}
            name={["customer", "phone"]}
            rules={[{ validator: validatePhone }]}
          >
            <PhoneInput />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.customer.emailOptional")}
            name={["customer", "email"]}
            rules={[
              { type: "email", message: t("leadWizard.customer.emailInvalid") }
            ]}
            normalize={(value: string) => normalizeEmail(value) || null}
          >
            <TextInput disableAutoCapitalize placeholder={placeholders.email} />
          </Form.Item>
        </Col>
      </Row>

      <Alert
        type={"info"}
        showIcon
        message={t("leadWizard.customer.minimumHint")}
        description={t("leadWizard.customer.minimumHintDescription")}
      />
    </>
  )
}
